import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, SafeAreaView } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { getLikes, getRepostedBy, getFollows, getFollowers, getQuotes } from '../services/bluesky';
import { Theme, useThemedStyles, useTheme } from '../theme';

interface UserListProps {
  type: 'likes' | 'reposts' | 'follows' | 'followers' | 'quotes';
  uri?: string; // For likes, reposts, quotes
  actor?: string; // For follows, followers
  onBack: () => void;
  onSelectUser: (did: string) => void;
  onSelectPost?: (post: any) => void; // For quotes
}

const UserList: React.FC<UserListProps> = ({ type, uri, actor, onBack, onSelectUser, onSelectPost }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const fetchData = useCallback(async (isNext = false) => {
    if (!isNext) setLoading(true);
    try {
      let res: any;
      if (type === 'likes' && uri) {
        res = await getLikes(uri, isNext ? cursor : undefined);
        const newUsers = res.likes.map((l: any) => l.actor);
        setUsers(prev => isNext ? [...prev, ...newUsers] : newUsers);
      } else if (type === 'reposts' && uri) {
        res = await getRepostedBy(uri, isNext ? cursor : undefined);
        setUsers(prev => isNext ? [...prev, ...res.repostedBy] : res.repostedBy);
      } else if (type === 'follows' && actor) {
        res = await getFollows(actor, isNext ? cursor : undefined);
        setUsers(prev => isNext ? [...prev, ...res.follows] : res.follows);
      } else if (type === 'followers' && actor) {
        res = await getFollowers(actor, isNext ? cursor : undefined);
        setUsers(prev => isNext ? [...prev, ...res.followers] : res.followers);
      } else if (type === 'quotes' && uri) {
        res = await getQuotes(uri, isNext ? cursor : undefined);
        setPosts(prev => isNext ? [...prev, ...res.posts] : res.posts);
      }
      setCursor(res?.cursor);
    } catch (error) {
      console.error('Failed to fetch user list:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [type, uri, actor, cursor]);

  useEffect(() => {
    fetchData();
  }, []);

  const getTitle = () => {
    switch (type) {
      case 'likes': return t('userList.likes');
      case 'reposts': return t('userList.reposts');
      case 'follows': return t('userList.follows');
      case 'followers': return t('userList.followers');
      case 'quotes': return t('userList.quotes');
      default: return t('userList.users');
    }
  };

  const renderUserItem = ({ item }: { item: any }) => (
    <TouchableOpacity style={styles.userItem} onPress={() => onSelectUser(item.did)}>
      <Image source={{ uri: item.avatar || 'https://via.placeholder.com/50' }} style={styles.avatar} />
      <View style={styles.userInfo}>
        <Text style={styles.displayName} numberOfLines={1}>{item.displayName || item.handle}</Text>
        <Text style={styles.handle} numberOfLines={1}>@{item.handle}</Text>
      </View>
      <Ionicons name="chevron-forward" size={20} color={theme.colors.textMuted} />
    </TouchableOpacity>
  );

  const renderPostItem = ({ item }: { item: any }) => (
    <TouchableOpacity style={styles.postItem} onPress={() => onSelectPost && onSelectPost(item)}>
      <View style={styles.postHeader}>
        <Image source={{ uri: item.author.avatar || 'https://via.placeholder.com/40' }} style={styles.postAvatar} />
        <View style={styles.postAuthorInfo}>
          <Text style={styles.postDisplayName} numberOfLines={1}>{item.author.displayName || item.author.handle}</Text>
          <Text style={styles.postHandle} numberOfLines={1}>@{item.author.handle}</Text>
        </View>
      </View>
      <Text style={styles.postText} numberOfLines={3}>{item.record?.text}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Ionicons name="chevron-back" size={28} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{getTitle()}</Text>
        <View style={{ width: 40 }} />
      </View>

      {loading && !refreshing && users.length === 0 && posts.length === 0 ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={type === 'quotes' ? posts : users}
          keyExtractor={(item) => item.did || item.uri}
          renderItem={type === 'quotes' ? renderPostItem : renderUserItem}
          onRefresh={() => {
            setRefreshing(true);
            fetchData();
          }}
          refreshing={refreshing}
          onEndReached={() => cursor && fetchData(true)}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>見つかりませんでした</Text>
            </View>
          }
        />
      )}
    </SafeAreaView>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    height: 50,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: theme.colors.cardSecondary,
  },
  userInfo: {
    flex: 1,
    marginLeft: 12,
  },
  displayName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  handle: {
    fontSize: 14,
    color: theme.colors.textMuted,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    color: theme.colors.textMuted,
    fontSize: 15,
  },
  postItem: {
    padding: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  postAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
  },
  postAuthorInfo: {
    marginLeft: 10,
  },
  postDisplayName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  postHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
  },
  postText: {
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 20,
  },
});

export default UserList;
